import { emptySplitApi } from '../../../../store/emptyApi';
import { AIPromptApiTags } from './AIPromptApiTags';

export interface PromptType {
  value: number;
  label: string;
}

export interface AIPrompt {
  id?: number;
  prompt_type: number;
  prompt_type_display: string;
  instructions: string;
  example: string;
  updated_at?: string;
  updated_by?: number;
  version?: number;
}

export interface PromptPreviewResult {
  prompt_type: number;
  prompt_type_display: string;
  system_message: string;
  prompt: string;
  mock_data: Record<string, unknown> | null;
  output_schema: Record<string, unknown> | null;
}

export interface PromptTestResult {
  prompt_type: number;
  prompt_type_display: string;
  prompt: string;
  success: boolean;
  response: string | null;
  wrapped_response: Record<string, unknown> | null;
  error: string | null;
  model?: string;
  usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  duration_ms: number;
}

// Matches ai.models.EvalRunStatus
export const EvalRunStatus = {
  Pending: 1,
  Running: 2,
  Succeeded: 3,
  Failed: 4,
  Superseded: 5,
} as const;
export type EvalRunStatusValue = (typeof EvalRunStatus)[keyof typeof EvalRunStatus];

/** One fixture case of a chat eval (carried on the "expectations" tier). */
export interface EvalCaseResult {
  id: string;
  name: string;
  category: string;
  passed: boolean;
  problems: string[];
  error: string | null;
  judge_score: number | null;
  judge_rationale: string;
}

export interface EvalTierResult {
  name: string;
  tier: 'structural' | 'content' | 'judge';
  passed: boolean;
  score: number | null;
  max_score: number | null;
  details: string;
  skipped: boolean;
  cases?: EvalCaseResult[];
}

export type EvalGrade = 'good' | 'warn' | 'fail' | '';

/** Matches ai.models.ProposalStatus; '' = no fix has been requested. */
export type ProposalStatus = 'pending' | 'succeeded' | 'failed' | '';

export interface EvalRecommendation {
  source: string;
  severity: 'high' | 'medium' | 'low';
  issue: string;
  recommendation: string;
}

/** What an eval run targets: a close-pipeline prompt or the Copilot RAG instructions. */
export type EvalSubject = { kind: 'prompt'; promptType: number } | { kind: 'rag' };

export const evalSubjectId = (subject: EvalSubject) =>
  subject.kind === 'rag' ? 'rag' : subject.promptType;

const evalBaseUrl = (subject: EvalSubject) =>
  subject.kind === 'rag'
    ? '/api/admin/v1/rag-instructions/evals/'
    : `/api/admin/v1/ai-prompts/${subject.promptType}/evals/`;

export interface AIPromptEvalRun {
  id: number;
  subject_kind: 'prompt' | 'rag';
  prompt_type: number | null;
  prompt_version: number;
  status: EvalRunStatusValue;
  status_display: string;
  is_stale: boolean;
  passed: boolean | null;
  grade: EvalGrade;
  tier_results: EvalTierResult[];
  recommendations: EvalRecommendation[];
  judge_score: number | null;
  judge_rationale: string;
  /** {section_key: revised_text}; empty until a fix has been proposed */
  proposed_sections: Record<string, string>;
  proposal_changelog: string;
  proposal_created_at: string | null;
  /** Drafting runs in Celery; poll latest while 'pending'. */
  proposal_status: ProposalStatus;
  proposal_error: string;
  error: string;
  call_count: number;
  usage: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  };
  duration_ms: number;
  model: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface LatestEvalResponse {
  supported: boolean;
  eval_run: AIPromptEvalRun | null;
}

const taggedApi = emptySplitApi.enhanceEndpoints({
  addTagTypes: Object.values(AIPromptApiTags),
});

const aiPromptApi = taggedApi.injectEndpoints({
  endpoints: (build) => ({
    getPromptTypes: build.query<PromptType[], void>({
      query: () => ({
        url: '/api/admin/v1/ai-prompts/types/',
        method: 'GET',
      }),
      providesTags: [AIPromptApiTags.AIPromptTypes],
    }),
    getAllPrompts: build.query<AIPrompt[], void>({
      query: () => ({
        url: '/api/admin/v1/ai-prompts/',
        method: 'GET',
      }),
      providesTags: [AIPromptApiTags.AIPrompt],
    }),
    getPrompt: build.query<AIPrompt, number>({
      query: (promptType) => ({
        url: `/api/admin/v1/ai-prompts/${promptType}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, promptType) => [
        { type: AIPromptApiTags.AIPrompt, id: promptType },
      ],
    }),
    updatePrompt: build.mutation<AIPrompt, { promptType: number; instructions: string; example: string }>({
      query: ({ promptType, instructions, example }) => ({
        url: `/api/admin/v1/ai-prompts/${promptType}/`,
        method: 'PUT',
        body: { instructions, example },
      }),
      invalidatesTags: (_result, _error, { promptType }) => [
        { type: AIPromptApiTags.AIPrompt, id: promptType },
        AIPromptApiTags.AIPrompt,
        // Saving bumps the prompt version, so the latest eval turns stale
        // ("not evaluated") — refetch it to reflect that immediately.
        { type: AIPromptApiTags.AIPromptEval, id: promptType },
      ],
    }),
    previewPrompt: build.mutation<PromptPreviewResult, { promptType: number; instructions?: string; example?: string }>({
      query: ({ promptType, instructions, example }) => ({
        url: `/api/admin/v1/ai-prompts/${promptType}/preview/`,
        method: 'POST',
        body: {
          ...(instructions !== undefined ? { instructions } : {}),
          ...(example !== undefined ? { example } : {}),
        },
      }),
    }),
    testPrompt: build.mutation<PromptTestResult, { promptType: number; instructions?: string; example?: string }>({
      query: ({ promptType, instructions, example }) => ({
        url: `/api/admin/v1/ai-prompts/${promptType}/test/`,
        method: 'POST',
        body: {
          ...(instructions !== undefined ? { instructions } : {}),
          ...(example !== undefined ? { example } : {}),
        },
      }),
    }),
    getLatestEval: build.query<LatestEvalResponse, EvalSubject>({
      query: (subject) => ({
        url: `${evalBaseUrl(subject)}latest/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, subject) => [
        { type: AIPromptApiTags.AIPromptEval, id: evalSubjectId(subject) },
      ],
    }),
    runEval: build.mutation<AIPromptEvalRun, EvalSubject>({
      query: (subject) => ({
        url: evalBaseUrl(subject),
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, subject) => [
        { type: AIPromptApiTags.AIPromptEval, id: evalSubjectId(subject) },
      ],
    }),
    proposeFix: build.mutation<AIPromptEvalRun, { subject: EvalSubject; evalId: number }>({
      query: ({ subject, evalId }) => ({
        url: `${evalBaseUrl(subject)}${evalId}/propose-fix/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { subject }) => [
        { type: AIPromptApiTags.AIPromptEval, id: evalSubjectId(subject) },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPromptTypesQuery,
  useGetAllPromptsQuery,
  useGetPromptQuery,
  useUpdatePromptMutation,
  usePreviewPromptMutation,
  useTestPromptMutation,
  useGetLatestEvalQuery,
  useRunEvalMutation,
  useProposeFixMutation,
} = aiPromptApi;
export { aiPromptApi };
